/* eslint-disable @typescript-eslint/no-unsafe-function-type */
type WindowWithDataLayer = Window & {
  gtag: Function;
  grecaptcha: {
    execute: Function;
  };
};

declare const window: WindowWithDataLayer;

const grecaptcha = window.grecaptcha || {};

const TARGET_ID = import.meta.env
  .VITE_REACT_APP_GOOGLE_ANALYTICS_MEASUREMENT_ID!;

// GA4 is loaded through GTM, so `gtag('get', ...)` only calls back once the
// container and its GA4 tag have actually run. Cap the wait so a blocked or
// slow container can never hold up a payment.
const GTAG_GET_TIMEOUT_MS = 500;

// GA4's default session timeout. A cookie older than this names a session that
// has already ended.
const GA_SESSION_TIMEOUT_MS = 30 * 60 * 1000;

type GoogleAnalyticsField = 'client_id' | 'session_id';

type GoogleAnalyticsIds = {
  gClientId: string;
  gSessionId: string;
};

function gtag(...params: any[]) {
  window.gtag?.(...params);
}

function readCookie(name: string): string {
  try {
    const match = document.cookie.match(
      new RegExp(`(?:^|;\\s*)${name}=([^;]*)`),
    );
    return match ? decodeURIComponent(match[1]) : '';
  } catch {
    return '';
  }
}

/**
 * `_ga=GA1.1.811133795.1787690037` -> `811133795.1787690037`
 * The leading segments encode cookie version and domain depth, so the client id
 * is always the last two dot-separated parts.
 */
function readClientIdFromCookie(): string {
  const raw = readCookie('_ga');
  const parts = raw.split('.');

  return parts.length >= 4 ? parts.slice(-2).join('.') : '';
}

/**
 * `_ga_<CONTAINER>` holds the session state in one of two shapes:
 *   GS1.1.1787690037.1.1.1787690847.0.0.0
 *   GS2.1.s1787690037$o1$g1$t1787690847$j0$l0$h0
 * In both, the session id is the third dot-separated segment (GS2 prefixes it
 * with `s` and appends `$`-delimited counters) and the last hit timestamp is
 * `$t...` for GS2 or the sixth dot-separated field for GS1.
 */
function readSessionIdFromCookie(): string {
  if (!TARGET_ID) return '';

  const parts = readCookie(`_ga_${TARGET_ID.replace(/^G-/, '')}`).split('.');
  const payload = parts[2] || '';

  const sessionId = payload.match(/^s?(\d+)/)?.[1] || '';
  const lastHit = payload.match(/\$t(\d+)/)?.[1] || parts[5] || '';

  if (!sessionId || !lastHit) return '';

  // GA4 ends a session after 30 minutes of inactivity, so a stale cookie names
  // a session that no longer exists. Unlike the client id, which is stable for
  // years, an expired session id is worse than no session id at all - it would
  // be stored on the invoice and later reported against a dead session.
  if (Date.now() - Number(lastHit) * 1000 > GA_SESSION_TIMEOUT_MS) return '';

  return sessionId;
}

function readFromGtag(field: GoogleAnalyticsField): Promise<string> {
  return new Promise((resolve) => {
    let settled = false;

    const settle = (value: string) => {
      if (settled) return;
      settled = true;
      resolve(value || '');
    };

    const timer = setTimeout(() => settle(''), GTAG_GET_TIMEOUT_MS);

    if (!TARGET_ID || typeof window.gtag !== 'function') {
      clearTimeout(timer);
      settle('');
      return;
    }

    try {
      window.gtag('get', TARGET_ID, field, (value: string) => {
        clearTimeout(timer);
        settle(value);
      });
    } catch {
      clearTimeout(timer);
      settle('');
    }
  });
}

const EMPTY_IDS: GoogleAnalyticsIds = { gClientId: '', gSessionId: '' };

/**
 * Live gtag first, GA's own cookies as the fallback. The cookies survive the
 * cases that used to silently yield an empty id: the GA4 tag not having
 * registered yet when the user clicks pay, or the `get` callback never firing.
 *
 * Never rejects. Analytics is best-effort and these ids are read inside the
 * payment flow, so a failure here must degrade to an empty id rather than
 * propagate to the caller.
 */
async function getGoogleAnalyticsClientId(): Promise<string> {
  try {
    const clientId = await readFromGtag('client_id');

    return clientId || readClientIdFromCookie();
  } catch (err) {
    console.log(err);
    return '';
  }
}

async function getGoogleAnalyticsSessionId(): Promise<string> {
  try {
    const sessionId = await readFromGtag('session_id');

    return sessionId || readSessionIdFromCookie();
  } catch (err) {
    console.log(err);
    return '';
  }
}

/**
 * Both ids for one request. Resolves them in parallel so the pay button waits
 * at most GTAG_GET_TIMEOUT_MS in total rather than once per id.
 *
 * Never rejects, for the same reason as the getters above.
 */
async function getGoogleAnalyticsIds(): Promise<GoogleAnalyticsIds> {
  try {
    const [gClientId, gSessionId] = await Promise.all([
      getGoogleAnalyticsClientId(),
      getGoogleAnalyticsSessionId(),
    ]);

    return { gClientId, gSessionId };
  } catch (err) {
    console.log(err);
    return EMPTY_IDS;
  }
}

function sendEvent(eventName: string, params: Record<string, any> = {}) {
  gtag('event', eventName, params);
}

async function reportSignup() {
  try {
    const gClientId = await getGoogleAnalyticsClientId();
    if (gClientId) {
      sendEvent('web_registration', { client_id: gClientId });
    }
  } catch (err) {
    console.log(err);
  }
}

async function reportAiTrace() {
  try {
    const gClientId = await getGoogleAnalyticsClientId();
    if (gClientId) {
      sendEvent('trace_completed', {
        client_id: gClientId,
        form_name: 'ai_investigation',
      });
    }
  } catch (err) {
    console.log(err);
  }
}

async function executeCaptcha() {
  return new Promise<string>((resolve, reject) => {
    grecaptcha
      .execute(import.meta.env.VITE_REACT_APP_GOOGLE_RECAPTCHA_KEY as string, {
        action: 'submit',
      })
      .then((token: string) => {
        resolve(token);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

export type { GoogleAnalyticsIds };

export {
  gtag,
  getGoogleAnalyticsClientId,
  getGoogleAnalyticsSessionId,
  getGoogleAnalyticsIds,
  sendEvent,
  reportSignup,
  executeCaptcha,
  reportAiTrace,
};
